import { APIResponse } from "../../../models/NetworkResponse";
import {
  SwayyCreateProductImagePayload,
  SwayyProductImage,
  SwayyProductImageType,
  SwayyReorderProductImagesPayload,
  SwayyUpdateProductImagePayload,
} from "../models/images/SwayyProductImage";
import {
  SwayyEligibleOption,
  SwayyOptionValue,
} from "../models/options/SwayyCatalogOption";
import {
  SwayyProductListParams,
  SwayyProductStatus,
} from "../models/products/SwayyProduct";
import { enrichSwayyProductActivityLog } from "../models/products/swayyActivityLogDisplay";
import {
  SwayyProductActivityLog,
  SwayyProductActivityLogChangeDetail,
  SwayyProductActivityLogFilters,
  SwayyProductActivityLogsPagination,
} from "../models/products/SwayyProductActivityLog";
import {
  SwayyProductCreatePayload,
  SwayyProductUpdatePayload,
} from "../models/products/SwayyProductForm";
import {
  SwayyProductOptionPayloadItem,
  SwayySelectedOption,
} from "../models/variants/SwayyProductOptions";
import { SwayyProductVariant } from "../models/variants/SwayyProductVariant";
import {
  SwayyCreateVariantFailure,
  SwayyCreateVariantItem,
  SwayyCreateVariantsResult,
  SwayyVariantPreviewRow,
} from "../models/variants/SwayyVariantCreate";
import {
  SwayyCreatePriceListPayload,
  SwayyPriceList,
  SwayyUpdatePriceListPayload,
} from "../models/pricing/SwayyPriceList";
import {
  SwayyUpsertPriceItem,
  SwayyVariantPrice,
} from "../models/pricing/SwayyVariantPrice";
import {
  deleteAPI,
  getAPI,
  patchAPI,
  postAPI,
  postFormDataAPI,
  putAPI,
} from "./Networking";
import {
  asBool,
  assertSuccess,
  getResponseData,
  pickNumber,
  pickString,
  requireRowArray,
  SwayyAPIResponse,
  SwayyApiError,
} from "./swayyApiHelpers";

export {
  isCancelledSwayyRequest,
  normalizeSwayyApiError,
  SwayyApiError,
} from "./swayyApiHelpers";

export interface SwayyAdminProductApi {
  id: number;
  sku: string | null;
  name: string;
  slug: string;
  brand_name: string | null;
  status: SwayyProductStatus;
  variant_count: number;
  thumbnail?: string | null;
  is_b2c?: boolean | null;
  is_b2b?: boolean | null;
  updated_admin: {
    id: number;
    name: string;
  } | null;
  updated_at: string;
}

interface SwayyProductsResponse {
  success: boolean;
  message: string;
  data: SwayyAdminProductApi[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

export interface SwayyBrandApi {
  id: number;
  name: string;
}

export interface SwayyCategoryApi {
  id: number;
  name: string;
  children?: SwayyCategoryApi[];
}

export interface SwayyProductDetailApi {
  id: number;
  uuid: string;
  sku: string | null;
  name: string;
  slug: string;
  brand_id: number | null;
  primary_category_id: number | null;
  description: string | null;
  status: SwayyProductStatus;
  is_b2c: boolean | null;
  is_b2b: boolean | null;
  hsn_code: string | null;
  tax_rate: number | null;
  attributes: unknown;
  created_admin: { id: number; name: string } | null;
  updated_admin: { id: number; name: string } | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/** Full admin product detail payload (GET + PATCH response). */
export interface SwayyProductDetailDataApi {
  product: SwayyProductDetailApi;
  categories: Array<{ id: number; name: string; slug: string }>;
  // TODO(next phases): replace unknown[] with real types when building
  // Variants / Images / SEO / options / sections / pricing tabs.
  selected_options: unknown[];
  variants: unknown[];
  images: unknown[] | SwayyProductImageApi[];
  sections: unknown[];
  feature_details: unknown[];
  price_summary: unknown[];
}

export interface SwayyProductImageApi {
  id: number;
  product_id: number;
  variant_id: number | null;
  url: string;
  alt: string | null;
  image_type: SwayyProductImageType | string;
  is_default: boolean;
  priority: number;
  width: number | null;
  height: number | null;
}

export const listSwayyProducts = async (
  params: SwayyProductListParams,
  signal?: AbortSignal,
): Promise<SwayyProductsResponse> => {
  const response = await getAPI<SwayyAPIResponse<SwayyAdminProductApi[]> & {
    pagination?: SwayyProductsResponse["pagination"];
  }>("/v1/catalog/admin/products", params, signal);
  assertSuccess(response);
  return {
    success: response.success,
    message: response.message,
    data: getResponseData(response),
    pagination: response.pagination ?? {
      total: 0,
      limit: params.limit,
      offset: params.offset,
    },
  };
};

export const listSwayyProductActivityLogs = async (
  productId: number,
  params: SwayyProductActivityLogFilters,
): Promise<{
  data: SwayyProductActivityLog[];
  pagination: SwayyProductActivityLogsPagination;
}> => {
  const response = await getAPI<
    SwayyAPIResponse<unknown> & {
      pagination?: SwayyProductActivityLogsPagination;
    }
  >(`/v1/catalog/admin/products/${productId}/activity-logs`, params);
  assertSuccess(response);
  return {
    data: requireRowArray(getResponseData(response))
      .map(mapSwayyProductActivityLog)
      .filter((log): log is SwayyProductActivityLog => log !== null),
    pagination: response.pagination ?? {
      total: 0,
      limit: params.limit,
      offset: params.offset,
    },
  };
};

const mapActivityChangeDetail = (
  value: unknown,
): SwayyProductActivityLogChangeDetail | null => {
  if (!value || typeof value !== "object") return null;
  const row = value as Record<string, unknown>;
  const field = pickString(row.field);
  const label = pickString(row.label);
  if (!field || !label) return null;
  const from = row.from;
  const to = row.to;
  const isScalar = (item: unknown) =>
    item === null ||
    typeof item === "string" ||
    typeof item === "number" ||
    typeof item === "boolean";
  return {
    field,
    label,
    from: isScalar(from) ? (from as string | number | boolean | null) : null,
    to: isScalar(to) ? (to as string | number | boolean | null) : null,
  };
};

const mapSwayyProductActivityLog = (
  value: unknown,
): SwayyProductActivityLog | null => {
  if (!value || typeof value !== "object") return null;
  const row = value as Record<string, unknown>;
  const id = pickNumber(row.id);
  const productId = pickNumber(row.product_id);
  const action = pickString(row.action);
  const createdAt = pickString(row.created_at);
  if (id == null || productId == null || !action || !createdAt) return null;
  const changeDetails = Array.isArray(row.change_details)
    ? row.change_details
        .map(mapActivityChangeDetail)
        .filter(
          (detail): detail is SwayyProductActivityLogChangeDetail =>
            detail !== null,
        )
    : [];
  const changes =
    row.changes && typeof row.changes === "object"
      ? (row.changes as Record<string, unknown>)
      : null;
  return enrichSwayyProductActivityLog({
    id,
    productId,
    productName: pickString(row.product_name),
    action,
    actionLabel: pickString(row.action_label) || action,
    entityType: pickString(row.entity_type) || "product",
    entityTypeLabel: pickString(row.entity_type_label) || "Product",
    summary: pickString(row.summary) || action,
    adminId: pickNumber(row.admin_id) ?? 0,
    adminName: pickString(row.admin_name),
    createdAt,
    changeDetails,
    changes,
  });
};

export const listSwayyBrands = async (
  signal?: AbortSignal,
): Promise<SwayyBrandApi[]> => {
  const response = await getAPI<SwayyAPIResponse<SwayyBrandApi[]>>(
    "/v1/catalog/admin/brands",
    { is_active: true, limit: 100 },
    signal,
  );
  return getResponseData(assertSuccess(response));
};

export const listSwayyCategories = async (
  signal?: AbortSignal,
): Promise<SwayyCategoryApi[]> => {
  const response = await getAPI<SwayyAPIResponse<SwayyCategoryApi[]>>(
    "/v1/catalog/admin/categories/tree",
    undefined,
    signal,
  );
  return getResponseData(assertSuccess(response));
};

export const getSwayyProductDetail = async (
  productId: number,
  signal?: AbortSignal,
): Promise<SwayyProductDetailDataApi> => {
  const response = await getAPI<SwayyAPIResponse<SwayyProductDetailDataApi>>(
    `/v1/catalog/admin/products/${productId}`,
    undefined,
    signal,
  );
  return getResponseData(assertSuccess(response));
};

export const createSwayyProduct = async (
  payload: SwayyProductCreatePayload,
): Promise<SwayyProductDetailDataApi> => {
  const response: APIResponse = await postAPI(
    "/v1/catalog/admin/products",
    payload,
  );
  assertSuccess(response);
  return (response as APIResponse & { data: SwayyProductDetailDataApi }).data;
};

export const updateSwayyProduct = async (
  productId: number,
  payload: SwayyProductUpdatePayload,
): Promise<SwayyProductDetailDataApi> => {
  const response = await patchAPI<SwayyAPIResponse<SwayyProductDetailDataApi>>(
    `/v1/catalog/admin/products/${productId}`,
    payload,
  );
  return getResponseData(assertSuccess(response));
};

export const updateSwayyProductCategories = async (
  productId: number,
  categoryIds: number[],
): Promise<void> => {
  const response: APIResponse = await putAPI(
    `/v1/catalog/admin/products/${productId}/categories`,
    { category_ids: categoryIds },
  );
  assertSuccess(response);
};

export const updateSwayyProductStatus = async (
  productId: number,
  status: SwayyProductStatus,
): Promise<void> => {
  const response = await patchAPI<SwayyAPIResponse<unknown>>(
    `/v1/catalog/admin/products/${productId}/status`,
    { status },
  );
  assertSuccess(response);
};

export const deleteSwayyProduct = async (
  productId: number,
): Promise<void> => {
  const response = await deleteAPI<
    SwayyAPIResponse<unknown> | null | undefined | ""
  >(`/v1/catalog/admin/products/${productId}`);
  // 204 No Content → empty body; treat as success
  if (response == null || response === "") return;
  assertSuccess(response);
};

export const deleteSwayyProductVariant = async (
  variantId: number,
): Promise<void> => {
  const response = await deleteAPI<
    SwayyAPIResponse<unknown> | null | undefined | ""
  >(`/v1/catalog/admin/variants/${variantId}`);
  // Soft-delete returns 204 with empty body — do not assertSuccess on empty
  if (response == null || response === "") return;
  assertSuccess(response);
};

/** Shared option-value mapper (eligible options + catalog create/update). */
export const mapSwayyOptionValue = (raw: unknown): SwayyOptionValue | null => {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const id = Number(row.id ?? row.value_id ?? row.option_value_id);
  if (!Number.isInteger(id) || id <= 0) return null;
  const meta =
    row.meta && typeof row.meta === "object" && !Array.isArray(row.meta)
      ? (row.meta as Record<string, unknown>)
      : null;
  return {
    id,
    name:
      pickString(row.value) ||
      pickString(row.name) ||
      pickString(row.label) ||
      pickString(row.title) ||
      `Value #${id}`,
    skuCode: pickString(row.sku_code) || "",
    position: pickNumber(row.position) ?? 0,
    isActive: asBool(row.is_active ?? row.active, true),
    hex: pickString(meta?.hex) || pickString(row.hex_code) || null,
  };
};

const mapOptionValues = (raw: unknown): SwayyOptionValue[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(mapSwayyOptionValue)
    .filter((value): value is SwayyOptionValue => value !== null);
};

export const mapSwayyEligibleOption = (
  raw: unknown,
): SwayyEligibleOption | null => {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const id = Number(row.id ?? row.option_id);
  if (!Number.isInteger(id) || id <= 0) return null;
  return {
    id,
    name:
      pickString(row.name) ||
      pickString(row.option_name) ||
      `Option #${id}`,
    displayType:
      pickString(row.display_type) || pickString(row.displayType) || "dropdown",
    values: mapOptionValues(
      row.values ?? row.option_values ?? row.active_values,
    ),
  };
};

export const mapSwayySelectedOption = (
  raw: unknown,
): SwayySelectedOption | null => {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const nested =
    row.option && typeof row.option === "object"
      ? (row.option as Record<string, unknown>)
      : null;
  const optionId = Number(row.option_id ?? nested?.id ?? row.id);
  if (!Number.isInteger(optionId) || optionId <= 0) return null;
  return {
    optionId,
    name:
      pickString(row.name) ||
      pickString(row.option_name) ||
      pickString(nested?.name) ||
      `Option #${optionId}`,
    position: pickNumber(row.position) ?? pickNumber(row.display_order) ?? 0,
  };
};

const formatVariantAttributes = (raw: Record<string, unknown>): string => {
  const attributeSource = raw.attributes ?? raw.option_values;
  if (Array.isArray(attributeSource) && attributeSource.length > 0) {
    const labels = attributeSource
      .map((item) => {
        if (typeof item === "string") return item.trim();
        if (!item || typeof item !== "object") return "";
        const row = item as Record<string, unknown>;
        const optionName =
          pickString(row.option_name) ||
          pickString(row.name) ||
          pickString(row.key);
        const value =
          pickString(row.value) ||
          pickString(row.option_value) ||
          pickString(row.label);
        if (optionName && value) return `${optionName}: ${value}`;
        return value || optionName || "";
      })
      .filter(Boolean);
    if (labels.length > 0) return labels.join(", ");
  }
  if (attributeSource && typeof attributeSource === "object" && !Array.isArray(attributeSource)) {
    const labels = Object.entries(attributeSource as Record<string, unknown>)
      .map(([key, value]) => {
        const label = pickString(value);
        return label ? `${key}: ${label}` : "";
      })
      .filter(Boolean);
    if (labels.length > 0) return labels.join(", ");
  }
  return (
    pickString(raw.option_name) && pickString(raw.name)
      ? `${pickString(raw.option_name)}: ${pickString(raw.name)}`
      : pickString(raw.title) || pickString(raw.name) || "—"
  );
};

export const mapSwayyProductVariant = (
  raw: unknown,
): SwayyProductVariant | null => {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const id = Number(row.id);
  if (!Number.isInteger(id) || id <= 0) return null;
  return {
    id,
    name: pickString(row.title) || pickString(row.name) || `Variant #${id}`,
    sku: pickString(row.sku) || "—",
    attributesLabel: formatVariantAttributes(row),
    cost: pickNumber(row.cost_price) ?? pickNumber(row.cost),
    active: asBool(row.is_active ?? row.active, true),
  };
};

export const listSwayyEligibleOptions = async (
  productId: number,
  signal?: AbortSignal,
): Promise<SwayyEligibleOption[]> => {
  const response = await getAPI<SwayyAPIResponse<unknown>>(
    `/v1/catalog/admin/products/${productId}/eligible-options`,
    undefined,
    signal,
  );
  assertSuccess(response);
  return requireRowArray(getResponseData(response))
    .map(mapSwayyEligibleOption)
    .filter((option): option is SwayyEligibleOption => option !== null);
};

export const listSwayyProductVariants = async (
  productId: number,
  signal?: AbortSignal,
): Promise<SwayyProductVariant[]> => {
  const response = await getAPI<SwayyAPIResponse<unknown>>(
    `/v1/catalog/admin/products/${productId}/variants`,
    undefined,
    signal,
  );
  assertSuccess(response);
  return requireRowArray(getResponseData(response))
    .map(mapSwayyProductVariant)
    .filter((variant): variant is SwayyProductVariant => variant !== null);
};

export const previewSwayyProductVariants = async (
  productId: number,
  selections: Array<{ option_id: number; value_ids: number[] }>,
): Promise<SwayyVariantPreviewRow[]> => {
  const response = await postAPI(
    `/v1/catalog/admin/products/${productId}/variants/preview`,
    { selections },
  );
  assertSuccess(response);
  const rows = requireRowArray(getResponseData(response));
  return rows.map((r: unknown) => {
    const row = r as Record<string, unknown>;
    return {
      combo: (row["combo"] as Record<string, number>) ?? {},
      label: String(row["label"] ?? ""),
      proposedSku: String(row["proposed_sku"] ?? ""),
      exists: Boolean(row["exists"]),
    } satisfies SwayyVariantPreviewRow;
  });
};

export const createSwayyProductVariants = async (
  productId: number,
  variants: SwayyCreateVariantItem[],
): Promise<SwayyCreateVariantsResult> => {
  const response = await postAPI(
    `/v1/catalog/admin/products/${productId}/variants`,
    { variants },
  );
  assertSuccess(response);
  const data = getResponseData(response);
  if (!data || typeof data !== "object") {
    return { createdCount: variants.length, failed: [] };
  }
  const payload = data as {
    created?: unknown;
    failed?: unknown;
  };
  const created = Array.isArray(payload.created) ? payload.created : [];
  const failedRaw = Array.isArray(payload.failed) ? payload.failed : [];
  const failed: SwayyCreateVariantFailure[] = failedRaw
    .map((item: unknown) => {
      if (!item || typeof item !== "object") return null;
      const row = item as Record<string, unknown>;
      return {
        index: Number(row["index"] ?? 0),
        code: String(row["code"] ?? ""),
        message: String(row["message"] ?? "Variant create failed."),
      } satisfies SwayyCreateVariantFailure;
    })
    .filter((item): item is SwayyCreateVariantFailure => item !== null);
  return {
    createdCount: created.length,
    failed,
  };
};

export const updateSwayyProductOptions = async (
  productId: number,
  options: SwayyProductOptionPayloadItem[],
): Promise<SwayySelectedOption[]> => {
  const response = await putAPI(
    `/v1/catalog/admin/products/${productId}/options`,
    { options },
  );
  assertSuccess(response);
  const detail = getResponseData(response) as SwayyProductDetailDataApi;
  const rows = Array.isArray(detail?.selected_options)
    ? detail.selected_options
    : [];
  if (rows.length > 0) {
    return rows
      .map(mapSwayySelectedOption)
      .filter((option): option is SwayySelectedOption => option !== null);
  }
  return options.map((item) => ({
    optionId: item.option_id,
    name: `Option #${item.option_id}`,
    position: item.position,
  }));
};

const IMAGE_TYPES: SwayyProductImageType[] = [
  "portrait",
  "landscape",
  "square",
];

const normalizeImageType = (value: unknown): SwayyProductImageType => {
  const raw = (pickString(value) ?? "").toLowerCase();
  return IMAGE_TYPES.includes(raw as SwayyProductImageType)
    ? (raw as SwayyProductImageType)
    : "square";
};

export const mapSwayyProductImage = (
  raw: unknown,
): SwayyProductImage | null => {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const id = Number(row.id);
  const productId = Number(row.product_id ?? row.productId);
  const url = pickString(row.url) ?? "";
  if (!Number.isInteger(id) || id <= 0 || !url) return null;
  const variantRaw = row.variant_id ?? row.variantId;
  const variantId =
    variantRaw == null || variantRaw === ""
      ? null
      : Number(variantRaw);
  return {
    id,
    productId: Number.isInteger(productId) && productId > 0 ? productId : 0,
    variantId:
      variantId != null && Number.isInteger(variantId) && variantId > 0
        ? variantId
        : null,
    url,
    alt: pickString(row.alt) ?? "",
    imageType: normalizeImageType(row.image_type ?? row.imageType),
    isDefault: asBool(row.is_default ?? row.isDefault, false),
    priority: pickNumber(row.priority) ?? 0,
    width: pickNumber(row.width),
    height: pickNumber(row.height),
  };
};

export const mapSwayyProductImages = (raw: unknown): SwayyProductImage[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(mapSwayyProductImage)
    .filter((image): image is SwayyProductImage => image !== null)
    .sort((a, b) => a.priority - b.priority || a.id - b.id);
};

/** Step 1: multipart upload → S3 URLs only (does not create product_images rows). */
export const uploadSwayyProductImages = async (
  productId: number,
  files: File[],
): Promise<string[]> => {
  if (!files.length) {
    throw new SwayyApiError("Please choose at least one image to upload.", 400);
  }
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  const response = await postFormDataAPI(
    `/v1/catalog/admin/products/${productId}/images/upload`,
    formData,
  );
  assertSuccess(response);
  const payload = getResponseData(response);
  if (Array.isArray(payload)) {
    return payload.filter((item): item is string => typeof item === "string");
  }
  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    for (const key of ["data", "urls", "images", "image_urls"]) {
      const candidate = record[key];
      if (Array.isArray(candidate)) {
        return candidate.filter(
          (item): item is string => typeof item === "string",
        );
      }
    }
  }
  throw new SwayyApiError("Upload succeeded but no image URLs were returned.");
};

/** Step 2: attach uploaded URL as a product_images row. */
export const createSwayyProductImage = async (
  productId: number,
  payload: SwayyCreateProductImagePayload,
): Promise<SwayyProductImage> => {
  const response = await postAPI(
    `/v1/catalog/admin/products/${productId}/images`,
    payload,
  );
  assertSuccess(response);
  const mapped = mapSwayyProductImage(getResponseData(response));
  if (!mapped) {
    throw new SwayyApiError("Image was created but response could not be read.");
  }
  return mapped;
};

export const updateSwayyProductImage = async (
  imageId: number,
  payload: SwayyUpdateProductImagePayload,
): Promise<SwayyProductImage> => {
  const response = await patchAPI<SwayyAPIResponse<unknown>>(
    `/v1/catalog/admin/images/${imageId}`,
    payload,
  );
  assertSuccess(response);
  const mapped = mapSwayyProductImage(getResponseData(response));
  if (!mapped) {
    throw new SwayyApiError("Image was updated but response could not be read.");
  }
  return mapped;
};

export const reorderSwayyProductImages = async (
  productId: number,
  payload: SwayyReorderProductImagesPayload,
): Promise<SwayyProductImage[]> => {
  const response = await putAPI(
    `/v1/catalog/admin/products/${productId}/images/reorder`,
    payload,
  );
  assertSuccess(response);
  return mapSwayyProductImages(getResponseData(response));
};

export const deleteSwayyProductImage = async (
  imageId: number,
): Promise<void> => {
  const response = await deleteAPI<
    SwayyAPIResponse<unknown> | null | undefined | ""
  >(`/v1/catalog/admin/images/${imageId}`);
  if (response == null || response === "") return;
  assertSuccess(response);
};

export const mapSwayyPriceList = (raw: unknown): SwayyPriceList | null => {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const id = Number(row.id);
  if (!Number.isInteger(id) || id <= 0) return null;
  const typeRaw = pickString(row.type)?.toLowerCase();
  const type = typeRaw === "b2b" ? "b2b" : "b2c";
  return {
    id,
    name: pickString(row.name) ?? `Price list #${id}`,
    type,
    currency: pickString(row.currency) ?? "INR",
    isDefault: asBool(row.is_default ?? row.isDefault, false),
    isActive: asBool(row.is_active ?? row.isActive, true),
  };
};

export const mapSwayyVariantPrice = (
  raw: unknown,
): SwayyVariantPrice | null => {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const id = Number(row.id);
  const variantId = Number(row.variant_id ?? row.variantId);
  const mrp = pickNumber(row.mrp);
  const salePrice = pickNumber(row.sale_price ?? row.salePrice);
  if (
    !Number.isInteger(id) ||
    id <= 0 ||
    !Number.isInteger(variantId) ||
    variantId <= 0 ||
    mrp == null ||
    salePrice == null
  ) {
    return null;
  }
  return {
    id,
    priceListId: pickNumber(row.price_list_id ?? row.priceListId) ?? 0,
    variantId,
    mrp,
    salePrice,
    minQty: pickNumber(row.min_qty ?? row.minQty) ?? 1,
    taxInclusive: asBool(row.tax_inclusive ?? row.taxInclusive, true),
  };
};

export const listSwayyPriceLists = async (
  signal?: AbortSignal,
): Promise<SwayyPriceList[]> => {
  const response = await getAPI<SwayyAPIResponse<unknown>>(
    "/v1/catalog/admin/price-lists",
    { limit: 50, offset: 0 },
    signal,
  );
  assertSuccess(response);
  return requireRowArray(getResponseData(response))
    .map(mapSwayyPriceList)
    .filter((list): list is SwayyPriceList => list !== null);
};

export const createSwayyPriceList = async (
  payload: SwayyCreatePriceListPayload,
): Promise<SwayyPriceList> => {
  const response = await postAPI("/v1/catalog/admin/price-lists", payload);
  assertSuccess(response);
  const mapped = mapSwayyPriceList(getResponseData(response));
  if (!mapped) {
    throw new SwayyApiError("Price list was created but response was empty.");
  }
  return mapped;
};

export const updateSwayyPriceList = async (
  priceListId: number,
  payload: SwayyUpdatePriceListPayload,
): Promise<SwayyPriceList> => {
  const response = await patchAPI<SwayyAPIResponse<unknown>>(
    `/v1/catalog/admin/price-lists/${priceListId}`,
    payload,
  );
  assertSuccess(response);
  const mapped = mapSwayyPriceList(getResponseData(response));
  if (!mapped) {
    throw new SwayyApiError("Price list was updated but response was empty.");
  }
  return mapped;
};

export const getSwayyDefaultB2cPriceList = async (
  signal?: AbortSignal,
): Promise<SwayyPriceList | null> => {
  const lists = await listSwayyPriceLists(signal);
  return (
    lists.find(
      (list) =>
        list.type === "b2c" && list.isDefault && list.isActive,
    ) ??
    lists.find((list) => list.type === "b2c" && list.isActive) ??
    null
  );
};

export const getSwayyVariantPrices = async (
  variantId: number,
  signal?: AbortSignal,
): Promise<SwayyVariantPrice[]> => {
  const response = await getAPI<SwayyAPIResponse<unknown>>(
    `/v1/catalog/admin/variants/${variantId}/prices`,
    undefined,
    signal,
  );
  assertSuccess(response);
  return requireRowArray(getResponseData(response))
    .map(mapSwayyVariantPrice)
    .filter((price): price is SwayyVariantPrice => price !== null);
};

export const upsertSwayyPrices = async (
  priceListId: number,
  prices: SwayyUpsertPriceItem[],
): Promise<SwayyVariantPrice[]> => {
  const response = await putAPI(
    `/v1/catalog/admin/price-lists/${priceListId}/prices`,
    { prices },
  );
  assertSuccess(response);
  return requireRowArray(getResponseData(response))
    .map(mapSwayyVariantPrice)
    .filter((price): price is SwayyVariantPrice => price !== null);
};
